import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';

interface AvgRankingPositionLineChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const AvgRankingPositionLineChartWidget: React.FC<AvgRankingPositionLineChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('AvgRankingPositionLineChartWidget raw data:', data);

  // Process the raw API data internally for Average Ranking Position
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('AvgRankingPositionLineChartWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const entries = Object.entries(rawData);
    
    if (entries.length === 0) {
      console.warn('AvgRankingPositionLineChartWidget: No data entries found');
      return null;
    }

    const firstEntry = entries[0];
    const [, firstDayData] = firstEntry;
    
    if (!firstDayData || typeof firstDayData !== 'object' || !(firstDayData as any).date) {
      console.warn('AvgRankingPositionLineChartWidget: Invalid data format');
      return null;
    }

    if (typeof (firstDayData as any).position !== 'number') {
      console.warn('AvgRankingPositionLineChartWidget: Missing required metric (position)');
      return null;
    }
    
    const chartData = entries.map(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object' || !dayData.date) {
        return null;
      }

      return {
        name: new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        position: dayData.position || 0
      };
    }).filter((item): item is { name: string; position: number } => item !== null)
      .sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime());

    return chartData.length > 0 ? chartData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={processedData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Line 
          type="monotone" 
          dataKey="position" 
          stroke={getChartColor(0, reportTheme.theme)} 
          strokeWidth={2} 
          name="Average Position"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
